import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
import collaborationCopy from '@/i18n/ns/workspace-collaboration';
import { formatCopy, namespaceCopyFor } from '@/i18n/ns';
// Shared chats in the workspace shell (collaboration v1, D57 decisions 1–3):
// the header's scope chip, "N following" and actions, Fork, the "Forked from"
// banner, the writer's "who can read" placeholder and the calm page a reader
// sees when a chat they had open is made private. The parts that live inside
// the thread (the read-only bar, suggested follow-ups, "Suggested by", the
// live status, Fork from here) are in chat-collaboration-thread.tsx, so the
// shell never loads the chat runtime.
//
// Only the owner writes. Nothing a reader does here can start a turn in
// someone else's chat, and nothing the owner does here sends one either:
// Ask only fills the owner's composer.
import { useEffect, useState, type ReactNode } from 'react';
import { Dialog } from 'radix-ui';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuNote,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { useCompany } from '@/company/context';
import { CompanyRequestError } from '@/company/api';
import { chatHash } from '@/lib/chat-links';
import { readThreadState } from '@/lib/chat-follow';
import type { ChatCopyKey } from '@/lib/chat-i18n';
import type { ServerThreadMeta } from '@/server-threads';
import { useOptionalWorkspace, useWorkspace, type WorkspaceState } from './context';
import { WorkspaceIcon, type WorkspaceIconName } from './icons';
import { ShareButton, ShareDialog } from './sharing';
import { sharingApi } from './sharing-api';
import { ShareChatButton } from './chat-sharing';
import { chatCollaborationApi } from './chat-collaboration-api';

type Visibility = ServerThreadMeta['visibility'];

const VISIBILITY_ICONS: Record<Visibility, WorkspaceIconName> = {
  private: 'lock',
  members: 'users',
  company: 'globe',
};

/** Where a chat lives, in the words every list and chip uses. */
export function chatVisibilityLabel(
  visibility: Visibility | undefined,
  companyName: string,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  if (visibility === 'company') return uiText('Everyone at {value1}', { value1: companyName });
  if (visibility === 'members') return uiText('Selected colleagues');
  return uiText('Only you');
}

export const firstName = (
  name: string | null | undefined,
  uiText: WorkspaceText = workspaceTextFor('en'),
) => name?.split(' ')[0] || uiText('The owner');

/** The open chat as its owner sees it. A saved chat of their own that the
 * server described without collaboration meta (a chat started in this tab)
 * is Personal until it is shared. */
export function ownedChatMeta(ws: WorkspaceState): ServerThreadMeta | null {
  if (ws.threadMeta) return ws.threadMeta;
  const saved = ws.threads.find((thread) => thread.id === ws.threadId);
  if (!saved) return null;
  return {
    id: saved.id,
    title: saved.title,
    access: 'owner',
    ownerName: ws.user?.displayName ?? null,
    visibility: 'private',
    audience: [],
    agentRunId: null,
    agentRun: null,
    forkedFrom: null,
    suggestedBy: {},
    toolSources: {},
  };
}

const isCompanyAdmin = (role: string | undefined) => role === 'owner' || role === 'admin';

/** Fork a chat (whole, or up to `messageId`) and open the copy: the person's
 * own Personal chat, which runs with their own connections. */
export async function forkAndOpen(
  ws: WorkspaceState,
  threadId: string,
  messageId?: string,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  const ownsNavigation = ws.beginNavigation();
  try {
    const { thread } = await chatCollaborationApi.fork(threadId, messageId);
    if (!ownsNavigation()) return;
    // The copy is the forker's own chat: list it, so it has its title and
    // a snapshot save at once.
    ws.refreshThreads();
    ws.openThread(thread.id);
    ws.notify(uiText('Forked. This copy is yours and runs with your own connections.'));
  } catch (failure) {
    if (!ownsNavigation()) return;
    ws.notify(
      failure instanceof CompanyRequestError && failure.status === 413
        ? uiText('This chat is too long to fork.')
        : uiText('We could not fork this chat. Please try again.'),
    );
  }
}

export function ForkButton({ compact = false }: { compact?: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const [busy, setBusy] = useState(false);
  return (
    <button
      className={`ws-button ${compact ? 'ws-chat-fork-compact' : 'primary'}`}
      disabled={busy}
      aria-label={uiText('Fork this chat')}
      onClick={() => {
        setBusy(true);
        void forkAndOpen(ws, ws.threadId, undefined, uiText).finally(() => setBusy(false));
      }}
    >
      <WorkspaceIcon name="fork" size={15} />
      <span>{busy ? uiText('Forking…') : uiText('Fork')}</span>
    </button>
  );
}

/** The header chip after the chat's title: where the chat lives. The owner
 * moves the chat from its menu; a company admin reading someone else's shared
 * chat can make it private from it (D57 decision 2). */
export function ChatScopeChip() {
  const { text: uiText, locale } = useWorkspaceText();
  const copy = namespaceCopyFor(collaborationCopy, locale);
  const ws = useWorkspace();
  const company = useCompany();
  const meta = ownedChatMeta(ws);
  if (!meta) return null;
  const owner = meta.access === 'owner';
  const label = owner
    ? chatVisibilityLabel(meta.visibility, ws.companyName, uiText)
    : formatCopy(copy.sharedBy, { name: firstName(meta.ownerName, uiText) });
  const chip = (
    <>
      <WorkspaceIcon name={VISIBILITY_ICONS[meta.visibility]} size={13} />
      <span>{label}</span>
    </>
  );
  if (owner) return <OwnerScopeMenu meta={meta}>{chip}</OwnerScopeMenu>;
  if (isCompanyAdmin(company.data?.membership?.role))
    return <AdminScopeMenu meta={meta}>{chip}</AdminScopeMenu>;
  return (
    <span className="ws-scope-chip static" data-visibility={meta.visibility}>
      {chip}
    </span>
  );
}

/** "2 people forked this chat. Their copies stay theirs." — read when the
 * confirmation opens (the owner's /state; an admin reads none). */
function useForkCount(meta: ServerThreadMeta, open: boolean) {
  const [people, setPeople] = useState<number | null>(null);
  useEffect(() => {
    if (!open || meta.access !== 'owner') return;
    const controller = new AbortController();
    void readThreadState(meta.id, { signal: controller.signal }).then((read) => {
      if (read.status === 'ok') setPeople(read.state.forkedBy ?? read.state.forks ?? 0);
    });
    return () => controller.abort();
  }, [open, meta.id, meta.access]);
  return people;
}

function forkNote(people: number | null, uiText: WorkspaceText = workspaceTextFor('en')) {
  if (people === null)
    return uiText(
      'Anyone who forked it keeps their copy: forks stay with the people who made them.',
    );
  if (people === 0) return uiText('Nobody has forked this chat.');
  return people === 1
    ? uiText('1 person forked this chat. Their copy stays theirs.')
    : uiText('{value1} people forked this chat. Their copies stay theirs.', { value1: people });
}

/** The Make private confirmation, for the owner and for an admin: who loses
 * access, that the owner is told (admin), and that forks stay. */
function MakePrivateDialog({
  meta,
  open,
  onOpenChange,
  onConfirm,
  byAdmin,
}: {
  meta: ServerThreadMeta;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  byAdmin: boolean;
}) {
  const { text: uiText, locale } = useWorkspaceText();
  const copy = namespaceCopyFor(collaborationCopy, locale);
  const ws = useWorkspace();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const people = useForkCount(meta, open);
  const owner = firstName(meta.ownerName, uiText);
  const followers = ws.threadLive?.followers ?? 0;
  const who = byAdmin ? formatCopy(copy.onlyOwner, { name: owner }) : uiText('Only you');
  async function confirm() {
    setBusy(true);
    setError('');
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : uiText('Could not make this private.'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ws-finder-scrim" />
        <Dialog.Content className="ws-share-dialog">
          <div className="ws-share-heading">
            <Dialog.Title>{uiText('Make this chat private?')}</Dialog.Title>
            <Dialog.Close className="ws-icon-button" aria-label={uiText('Close')}>
              <WorkspaceIcon name="close" />
            </Dialog.Close>
          </div>
          <Dialog.Description>
            {followers > 0
              ? formatCopy(copy.privateDescriptionFollowers, {
                  who,
                  title: meta.title,
                  followers: formatCopy(followers === 1 ? copy.followersOne : copy.followersMany, {
                    count: followers,
                  }),
                })
              : formatCopy(copy.privateDescription, { who, title: meta.title })}
            {byAdmin && ` ${formatCopy(copy.adminNotice, { owner })}`}
          </Dialog.Description>
          <p className="ws-page-note ws-fork-note">
            <WorkspaceIcon name="fork" size={14} />
            {byAdmin ? forkNote(null, uiText) : forkNote(people, uiText)}
          </p>
          {error && (
            <p className="ws-error" role="alert">
              {error}
            </p>
          )}
          <div className="ws-actions">
            <Dialog.Close className="ws-button">{uiText('Cancel')}</Dialog.Close>
            <button className="ws-button primary" disabled={busy} onClick={() => void confirm()}>
              {busy ? uiText('Saving…') : uiText('Make private')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function OwnerScopeMenu({ meta, children }: { meta: ServerThreadMeta; children: ReactNode }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState<Visibility | null>(null);
  const [confirming, setConfirming] = useState(false);
  async function makePrivate() {
    await sharingApi.save({
      kind: 'chat',
      id: meta.id,
      visibility: 'private',
      audience: [],
      revision: meta.sharingRevision,
    });
    ws.refreshThreadMeta();
    ws.notify(uiText('This chat is private again. Forks others made stay theirs.'));
  }
  const option = (visibility: Visibility, title: string, detail: string) => (
    <MenuRadioItem
      value={visibility}
      icon={<WorkspaceIcon name={VISIBILITY_ICONS[visibility]} size={16} />}
      description={detail}
      onSelect={() => {
        // Narrowing asks first (who loses access, and that forks stay).
        // Widening goes through the Share dialog, which warns when the chat
        // used the owner's private accounts.
        if (visibility === 'private') {
          if (meta.visibility !== 'private') setConfirming(true);
        } else setSharing(visibility);
      }}
    >
      {title}
    </MenuRadioItem>
  );
  return (
    <>
      <Menu open={open} onOpenChange={setOpen}>
        <MenuTrigger
          className="ws-scope-chip"
          data-visibility={meta.visibility}
          aria-label={uiText('Who can read this chat: {value1}', {
            value1: chatVisibilityLabel(meta.visibility, ws.companyName, uiText),
          })}
        >
          {children}
          <WorkspaceIcon name="down" size={12} />
        </MenuTrigger>
        <MenuContent label={uiText('Move this chat')} align="start">
          <MenuRadioGroup value={meta.visibility}>
            {option('private', uiText('Only you'), uiText('Personal. Nobody else sees it.'))}
            {option(
              'members',
              uiText('Selected colleagues'),
              uiText('People you pick. They get it in their Inbox.'),
            )}
            {option(
              'company',
              uiText('Everyone at {value1}', { value1: ws.companyName }),
              uiText('Everyone in the company can follow it and fork it.'),
            )}
          </MenuRadioGroup>
          <MenuSeparator />
          <MenuNote icon={<WorkspaceIcon name="eye" size={16} />}>
            {uiText('Only you write here. Others follow live and fork.')}
          </MenuNote>
        </MenuContent>
      </Menu>
      {sharing && (
        <Dialog.Root open onOpenChange={(next) => !next && setSharing(null)}>
          <ShareDialog
            kind="chat"
            item={chatShareItem(meta)}
            initialVisibility={sharing}
            onClose={() => setSharing(null)}
          />
        </Dialog.Root>
      )}
      <MakePrivateDialog
        meta={meta}
        open={confirming}
        onOpenChange={setConfirming}
        onConfirm={makePrivate}
        byAdmin={false}
      />
    </>
  );
}

function AdminScopeMenu({ meta, children }: { meta: ServerThreadMeta; children: ReactNode }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const owner = firstName(meta.ownerName, uiText);
  async function makePrivate() {
    await sharingApi.save({ kind: 'chat', id: meta.id, visibility: 'private', audience: [] });
    ws.notify(uiText('Made private. {value1} has been told in their Inbox.', { value1: owner }));
    ws.newChat();
  }
  if (meta.visibility === 'private')
    return <span className="ws-scope-chip static">{children}</span>;
  return (
    <>
      <Menu open={open} onOpenChange={setOpen}>
        <MenuTrigger className="ws-scope-chip" data-visibility={meta.visibility}>
          {children}
          <WorkspaceIcon name="down" size={12} />
        </MenuTrigger>
        <MenuContent label={uiText('Chat access')} align="start">
          <MenuNote icon={<WorkspaceIcon name={VISIBILITY_ICONS[meta.visibility]} size={16} />}>
            {uiRich('{value1}’s chat. As an admin you can make it private.', { value1: owner })}
          </MenuNote>
          <MenuSeparator />
          <MenuItem
            icon={<WorkspaceIcon name="lock" size={16} />}
            tone="danger"
            description={uiText('Only {value1} will see it. {value2} is told.', {
              value1: owner,
              value2: owner,
            })}
            onSelect={() => setConfirming(true)}
          >
            {uiText('Make private')}
          </MenuItem>
        </MenuContent>
      </Menu>
      <MakePrivateDialog
        meta={meta}
        open={confirming}
        onOpenChange={setConfirming}
        onConfirm={makePrivate}
        byAdmin
      />
    </>
  );
}

const chatShareItem = (meta: ServerThreadMeta) => ({
  id: meta.id,
  title: meta.title,
  visibility: meta.visibility,
  audience: meta.audience ?? [],
  sharingRevision: meta.sharingRevision,
  canShare: meta.access === 'owner',
});

/** "2 following" next to the header actions: live followers of the answer
 * being produced now. */
export function FollowingCount() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const followers = ws.threadLive?.followers ?? 0;
  if (followers < 1) return null;
  return (
    <span className="ws-following" role="status">
      {uiRich('{value1} {value2} following', {
        value1: <WorkspaceIcon name="eye" size={13} />,
        value2: followers,
      })}
    </span>
  );
}

/** Header actions for a chat: the owner shares it or saves a copy to
 * Library; a reader forks it. */
export function ChatHeaderActions() {
  const ws = useWorkspace();
  const meta = ownedChatMeta(ws);
  if (!meta) return null;
  if (meta.access !== 'owner')
    return (
      <div className="ws-chat-header-actions">
        <FollowingCount />
        <ForkButton compact />
      </div>
    );
  return (
    <div className="ws-chat-header-actions">
      <FollowingCount />
      <ShareButton kind="chat" item={chatShareItem(meta)} />
      <ShareChatButton />
    </div>
  );
}

/** A fork's link back to the chat it came from, while that chat is readable. */
export function ForkedFromBanner() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const from = ws.threadMeta?.forkedFrom;
  if (!from) return null;
  return (
    <div className="ws-forked-from" role="note">
      <WorkspaceIcon name="fork" size={14} />
      <span>
        {uiRich(
          'Forked from{value1} {value2} . The history is copied. From here, tools run with your own access.',
          {
            value1: ' ',
            value2: from.title ? (
              <a href={chatHash(from.threadId)}>
                {from.ownerName
                  ? uiText('{value1}’s chat ·', { value1: firstName(from.ownerName, uiText) }) + ' '
                  : ''}
                {from.title}
              </a>
            ) : (
              uiText('a chat you can no longer open')
            ),
          },
        )}
      </span>
    </div>
  );
}

/** The writer's composer placeholder, as a catalog key and its values (the
 * composer renders it in the person's language): who can read a chat that is
 * not Personal, or, in a fresh fork, that it continues with their own
 * connections. Null for an ordinary chat. */
export function useCollaborationPlaceholder(): {
  key: ChatCopyKey;
  values: Record<string, string | number>;
} | null {
  const ws = useOptionalWorkspace();
  const meta = ws?.threadMeta;
  if (!ws || !meta || meta.access !== 'owner') return null;
  if (meta.visibility === 'company')
    return { key: 'sharedChatEveryone', values: { company: ws.companyName } };
  if (meta.visibility === 'members') {
    const count = meta.audience?.length ?? 0;
    return count === 1
      ? { key: 'sharedChatOnePerson', values: {} }
      : { key: 'sharedChatPeople', values: { count } };
  }
  return meta.forkedFrom ? { key: 'forkedChatPlaceholder', values: {} } : null;
}

/** The calm page a reader sees when the chat they had open stops being
 * readable (its owner or an admin made it private, or they were taken off
 * its audience). Forks stay with the people who made them. */
export function ChatGone() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const owner = firstName(ws.threadMeta?.ownerName, uiText);
  return (
    <div className="ws-chat-gone" role="status">
      <WorkspaceIcon name="lock" size={28} />
      <h2>{uiRich('{value1} made this chat private', { value1: owner })}</h2>
      <p>{uiText('You can no longer open it. Any fork you made stays yours.')}</p>
      <div className="ws-actions">
        <button className="ws-button" onClick={() => ws.newChat()}>
          {uiText('Start a new chat')}
        </button>
        <button className="ws-button primary" onClick={() => ws.setRecentOpen(true)}>
          {uiText('Back to Recent chats')}
        </button>
      </div>
    </div>
  );
}
